import { useCallback, useState } from "react";
import { ProphetsCarousel } from "./ProphetsCarousel";
import { PROPHETS, INITIAL_ACTIVE_ID } from "@/config/prophetsData";

/**
 * Section — Meet the Prophets. Renders the header, an ambient sci-fi
 * backdrop (SVG orbital arcs + tiny stars) and the 3D carousel.
 *
 * The word "PROPHETS" inside the title reacts to the currently-active
 * carousel card by consuming its `accent` value as a CSS custom property.
 * "MEET THE" stays silver / white. Everything else — carousel timing,
 * transitions, interactions, autoplay — is unchanged.
 */
const INITIAL = PROPHETS.find((p) => p.id === INITIAL_ACTIVE_ID) || PROPHETS[0];

export function MeetTheProphets() {
  const [activeProphet, setActiveProphet] = useState(INITIAL);

  const handleActive = useCallback((p) => {
    if (p && p.id !== activeProphet.id) setActiveProphet(p);
  }, [activeProphet.id]);

  return (
    <section
      id="collection"
      data-testid="meet-the-prophets-section"
      className="relative w-full pt-6 pb-24 md:pb-32 px-5 md:px-10"
    >
      {/* Ambient background layer */}
      <div
        aria-hidden="true"
        className="prophets-orbits-wrap absolute inset-0 -z-0 pointer-events-none"
      >
        <svg
          viewBox="0 0 1600 900"
          preserveAspectRatio="xMidYMid slice"
          className="w-full h-full opacity-70"
        >
          <defs>
            <radialGradient id="prophets-orbit-glow" cx="50%" cy="55%" r="55%">
              <stop offset="0%"   stopColor="#4d5a7a" stopOpacity="0.16" />
              <stop offset="60%"  stopColor="#0a0c14" stopOpacity="0" />
              <stop offset="100%" stopColor="#000000" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="prophets-arc-stroke" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%"   stopColor="rgba(200,210,240,0)" />
              <stop offset="50%"  stopColor="rgba(200,210,240,0.28)" />
              <stop offset="100%" stopColor="rgba(200,210,240,0)" />
            </linearGradient>
          </defs>
          <rect width="1600" height="900" fill="url(#prophets-orbit-glow)" />
          {[280, 340, 400, 470].map((ry, i) => (
            <ellipse
              key={ry}
              cx="800" cy="470" rx={640 + i * 60} ry={ry}
              fill="none"
              stroke="url(#prophets-arc-stroke)"
              strokeWidth="0.7"
            />
          ))}
          {Array.from({ length: 46 }).map((_, i) => {
            const x = (i * 137) % 1600;
            const y = 120 + ((i * 191) % 620);
            const r = i % 9 === 0 ? 1.4 : 0.7;
            const op = i % 5 === 0 ? 0.85 : 0.35;
            return <circle key={i} cx={x} cy={y} r={r} fill="#ffffff" opacity={op} />;
          })}
          <circle cx="320"  cy="820" r="2.2" fill="#a4a6ff" opacity="0.55" />
          <circle cx="1280" cy="820" r="2.2" fill="#a4a6ff" opacity="0.55" />
        </svg>
      </div>

      <header className="relative z-10 max-w-[1180px] mx-auto text-center mb-10 md:mb-14">
        <h2
          data-testid="meet-title"
          className="p-nav text-2xl sm:text-3xl md:text-4xl lg:text-[42px] tracking-[0.28em] uppercase text-white"
        >
          <span
            data-testid="meet-title-static"
            className="prophets-meet-title-static"
          >
            Meet the{" "}
          </span>
          <span
            key={activeProphet.id}
            data-testid="meet-title-dynamic"
            data-active-family={activeProphet.family}
            className="prophets-meet-title-dynamic"
            style={{
              "--active-prophet-accent": activeProphet.accent || "#c9d1e0",
              "--active-prophet-accent-2":
                activeProphet.secondaryAccent || activeProphet.accent || "#c9d1e0",
            }}
          >
            Prophets
            <span aria-hidden="true" className="prophets-meet-title-shimmer" />
          </span>
        </h2>
        <p
          data-testid="meet-subtitle"
          className="p-mono mt-3 text-[10px] md:text-xs tracking-[0.4em] uppercase text-white/45"
        >
          10 Families. One Collection.
        </p>
      </header>

      <div className="relative z-10">
        <ProphetsCarousel prophets={PROPHETS} onActiveChange={handleActive} />
      </div>
    </section>
  );
}
